import * as React from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  BadgeIcon,
  InfoTooltip,
  SimpleAccordion,
  SimpleAccordionDetails,
  SimpleAccordionSummary,
} from '@sales-center/components'
import { ILeadOppProduct } from '@user/store/leadOppProducts/typings'
import { ServiceList } from '../ServiceList/ServiceList'
import { IServiceFrequency } from '@user/store/serviceFrequency/typings'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { useData, useHandlers } from './hooks'
import { ReOrderServicesButton } from './styled'
import { ReOrderServicesDrawer } from '../ReorderServicesDrawer/ReOrderServicesDrawer'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { Theme, useTheme } from '@mui/material/styles'

interface Props {
  onEditClick?: (service: ILeadOppProduct) => void
  disabled: boolean
  serviceFrequencies: IServiceFrequency[]
  minimumServicesCount?: number
  deleteServiceWarningMessage?: string
  entityType: ENTITY_TYPES
  localMode: boolean
  readOnly?: boolean
  loading: boolean
  location: LocationDTO
  defaultExpanded?: boolean
  totalServicesCount?: number
  disableGrayBg?: boolean
  variant: 'old' | 'new'
  showReorderServices?: boolean
}

export function ServiceLocationCardDetail(props: Props) {
  const {
    entityType,
    onEditClick = () => {},
    serviceFrequencies,
    minimumServicesCount,
    deleteServiceWarningMessage,
    loading,
    localMode,
    location,
    disabled,
    readOnly,
    defaultExpanded,
    totalServicesCount,
    disableGrayBg = false,
    showReorderServices = false,
    variant,
  } = props

  const {
    getStyles,
    fullName,
    services,
    reOrderDrawerOpen,
    setReOrderDrawerOpen,
    sortedServices,
  } = useData({ localMode, location })

  const { handleCloseReOrderDrawer, handleReOrderServicesButtonClick } =
    useHandlers({
      setReOrderDrawerOpen,
    })
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <>
      <SimpleAccordion
        square
        defaultExpanded={defaultExpanded}
        sx={{
          backgroundColor: 'transparent',
          margin: (theme: Theme) => theme.spacing(0),
        }}
      >
        {variant === 'new' ? (
          <SimpleAccordionSummary
            expandIcon={
              <ExpandMoreIcon data-test-id="service-location-card-details-expandIcon" />
            }
            style={{
              alignItems: 'start',
              marginBottom: theme.spacing(1.2),
              backgroundColor: disableGrayBg ? undefined : 'rgb(250, 250, 250)',
            }}
          >
            <Stack direction="row">
              <Box sx={styles.leftTitle}>Service Location:&nbsp;</Box>
              <Box sx={styles.leftTitleContent} whiteSpace="normal">
                {fullName}
              </Box>
            </Stack>
          </SimpleAccordionSummary>
        ) : (
          <Stack
            direction="row"
            sx={{
              alignItems: 'start',
              marginBottom: (theme: Theme) => theme.spacing(1.6),
              backgroundColor: disableGrayBg ? undefined : 'rgb(250, 250, 250)',
            }}
          >
            <Box>
              <BadgeIcon />
            </Box>
            <Box
              sx={{
                marginTop: (theme: Theme) => theme.spacing(0.3),
                marginLeft: (theme: Theme) => theme.spacing(0.5),
              }}
            >
              <Typography variant="body2">
                Service Location: {fullName}
              </Typography>
            </Box>
          </Stack>
        )}

        <SimpleAccordionDetails>
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction="column">
              {showReorderServices && services.length > 1 && (
                <Box display="flex" justifyContent="end" paddingBottom="12px">
                  <ReOrderServicesButton
                    onClick={handleReOrderServicesButtonClick}
                    variant="outlined"
                    disabled={readOnly}
                    data-test-id="re-order-services-button"
                  >
                    Re-Order Services
                  </ReOrderServicesButton>
                  <Box alignItems="center" display="flex" ml={1}>
                    <InfoTooltip
                      infoText="Select the order you want services to appear in the agreement. Service order is also reflected in the Closed Won Queue when creating service setups and/or service orders."
                      tooltipPlacement="top"
                    />
                  </Box>
                </Box>
              )}
              <ServiceList
                entityType={entityType}
                onEditClick={onEditClick}
                serviceFrequencies={serviceFrequencies}
                minimumServicesCount={minimumServicesCount}
                deleteWarningMessage={deleteServiceWarningMessage}
                localMode={localMode}
                disableEdit={loading || disabled}
                readOnly={readOnly}
                services={sortedServices}
                totalServicesCount={totalServicesCount}
                variant={variant}
              />
            </Stack>
          )}
        </SimpleAccordionDetails>
      </SimpleAccordion>
      <ReOrderServicesDrawer
        open={reOrderDrawerOpen}
        onClose={handleCloseReOrderDrawer}
        services={services}
        entityType={entityType}
      />
    </>
  )
}
